import React, { Component, type Ref } from 'react';
import memoizeOne from 'memoize-one';
import * as R from 'ramda';
import type { Position } from '@atlassian/jira-portfolio-3-common/src/drag-observer/types';
import { connect as connectWrapper } from '@atlassian/jira-react-redux';
import {
	ON_DRAG,
	ON_DRAG_START,
	ON_DRAG_END,
	ON_LENGTH_CHANGE,
	ON_RESIZE,
	SAVE_PREDRAG_WIDTHS,
	SET_ONBOARDING_WIDTH,
	SET_ACTIVE_SEPARATOR,
	SET_IS_HOVERED,
	type Dispatch,
} from './actions';
import ColumnTexts, { Updater as ColumnTextsUpdater } from './column-texts';
import Container from './container';
import SectionsContext, { SectionContext } from './context';
import Item from './item';
import withNamespacedId from './namespaced-id';
import Provider from './provider';
import Separator from './separator';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type {
	ColumnTextState,
	State,
	Props,
	SectionsContext as Parent,
	SectionData,
	OnResizePayload,
	OnDragPayload,
	SubscriberOwnProps,
	SubscriberHocProps,
	DispatchProps,
	OwnProps,
	StateProps,
	SectionsData,
} from './types';
import { storeKey } from './util';
// eslint-disable-next-line jira/react/no-class-components
class Sections extends Component<Props> {
	// eslint-disable-next-line react/sort-comp
	parent?: Parent | null;

	// The section component should at-least resize once to generate it's initial state in the redux store.
	// This flag is to keep track of the mandatory resize call.
	didPerformResizeAtLeastOnce = false;

	componentDidMount() {
		const { width } = this.props;

		if (width > 0) {
			this.onResize({ width });
		}
	}

	shouldComponentUpdate(nextProps: Props) {
		const isParentInSync =
			(!this.parent && !nextProps.linkToParent) ||
			(this.parent && this.parent.id === nextProps.linkToParent);
		const propsEqual = R.equals(this.props, nextProps);

		if (this.props.id !== nextProps.id) {
			this.didPerformResizeAtLeastOnce = false;
		}

		return !!(isParentInSync && !propsEqual);
	}

	componentDidUpdate(prevProps: Props) {
		const { id, totalWidth, sectionsData, width, isFitParent = true } = this.props;
		const toIds = R.map(R.prop('id'));
		if (!R.equals(toIds(sectionsData), toIds(prevProps.sectionsData))) {
			this.props.onLengthChange({
				id,
				width: totalWidth,
				sectionsData,
				parent: this.parent,
				isFitParent,
			});
		}

		// shouldComponentUpdate prevents the component update until the parent IDs are in sync.
		// But by the time parent IDs comes to sync, there might not be a change in width or id anymore
		// so the resize might not be called at all. The 'didPerformResizeAtLeastOnce' flag here ensures
		// at-least we call resize once for sections component after the parent IDs are in sync.
		if (width !== prevProps.width || id !== prevProps.id || !this.didPerformResizeAtLeastOnce) {
			this.onResize({ width });
			this.didPerformResizeAtLeastOnce = true;
		}
	}

	onDrag = ({ index, from, to }: { index: number; from: Position; to: Position }) => {
		const { sectionsData, id, totalWidth, isFitParent = true } = this.props;
		const delta = to.x - from.x;
		if (delta === 0) {
			return;
		}
		this.props.onDrag({
			id,
			index,
			delta,
			sectionsData,
			totalWidth,
			parent: this.parent,
			isFitParent,
		});
	};

	onDragStart = (index: number) => {
		const { id, sectionsData, savePreDragWidths, onDragStart, onDragStartOwn } = this.props;
		onDragStartOwn(id);
		savePreDragWidths(sectionsData[index].id);
		if (onDragStart) onDragStart();
	};

	onDragEnd = () => {
		const { id, onDragEnd, onDragEndOwn } = this.props;
		onDragEndOwn(id);
		if (onDragEnd) onDragEnd();
	};

	onCollapsedSectionClick = (index: number) => {
		const { sectionsData, id, totalWidth, isFitParent = true } = this.props;

		this.props.onDrag({
			id,
			index,
			sectionsData,
			totalWidth,
			parent: this.parent,
			isFitParent,
		});
	};

	onResize = ({ width }: { width: number }) => {
		const { sectionsData, id, isFitParent = true, minWidth } = this.props;
		this.props.onResize({
			id,
			width,
			sectionsData,
			parent: this.parent,
			isFitParent,
			minWidth,
		});
	};

	// this has been used in the src/packages/portfolio-3/portfolio/src/app-simple-plans/view/main/tabs/roadmap/view.tsx
	// eslint-disable-next-line react/no-unused-class-component-methods
	setOnboardingWidth = () => {
		const { id, sectionsData, width } = this.props;
		this.props.setOnboardingWidth(id, sectionsData, width);
	};

	// this has been used in the src/packages/portfolio-3/portfolio/src/app-simple-plans/view/main/tabs/roadmap/view.tsx
	// eslint-disable-next-line react/no-unused-class-component-methods
	collapseSection = (index: number) => {
		const { sectionsData, id, totalWidth, isFitParent = true } = this.props;
		const section = sectionsData[index];
		const delta = section.minWidth - section.width;
		this.props.savePreDragWidths(sectionsData[index].id);
		this.props.onDrag({
			id,
			index,
			sectionsData,
			delta,
			totalWidth,
			parent: this.parent,
			isFitParent,
		});
	};

	content = (parent?: Parent | null) => {
		this.parent = parent;

		const {
			id,
			sectionsData,
			sectionRenderer,
			height,
			minHeight,
			setActiveSeparator,
			activeSeparatorIndex,
			totalWidth,
			isDragging,
			hoveredSectionIndex,
			setHovered,
		} = this.props;
		const children = [];
		const columnTexts: Array<ColumnTextState> = [];

		for (let i = 0; i < sectionsData.length; i++) {
			const sectionEl = sectionRenderer({
				section: sectionsData[i],
				onSectionClick: this.onCollapsedSectionClick,
				index: i,
				isDragging,
				hoveredSectionIndex,
				setHovered,
			});

			const { title, icon, tooltip, width = 0, minWidth, collapsible } = sectionEl.props;
			const visible = collapsible && !!width && width <= minWidth;

			columnTexts.push({
				title,
				icon,
				tooltip,
				width,
				visible,
			});

			children.push(
				<SectionsContext.Provider
					key={sectionsData[i].id}
					value={{ id, index: i, sectionsData, totalWidth, parent }}
				>
					{sectionEl}
				</SectionsContext.Provider>,
			);
			if (i !== sectionsData.length - 1) {
				children.push(
					<Separator
						testId={`separator-${sectionsData[i].id}`}
						key={`separator-${sectionsData[i].id}`}
						onDrag={this.onDrag}
						onDragStart={this.onDragStart}
						onDragEnd={this.onDragEnd}
						index={i}
						setActiveSeparator={setActiveSeparator}
						isActive={activeSeparatorIndex === i}
						isDragging={isDragging}
					/>,
				);
			}
		}

		const shouldUpdateColumnTexts = columnTexts.every((it) => it.title);
		return (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			<div className={styles.sections} style={{ height, minHeight }}>
				{children}
				{shouldUpdateColumnTexts && <ColumnTextsUpdater id={id} columnTexts={columnTexts} />}
			</div>
		);
	};

	render() {
		const { linkToParent = false, sectionsData, onRender } = this.props;
		if (onRender) {
			onRender(sectionsData);
		}
		return linkToParent ? (
			<SectionsContext.Consumer>{(parent) => this.content(parent)}</SectionsContext.Consumer>
		) : (
			this.content()
		);
	}
}

type mapStateToPropsFactoryFn = (state: State, ownProps: OwnProps) => StateProps;
const sectionStateObj = {};
const widthObj = {};
const mapStateToPropsFactory = (): mapStateToPropsFactoryFn => {
	const getSectionsDataSelectorInstance = memoizeOne(
		(sectionsData: SectionsData, widths: Record<string, number>) =>
			sectionsData.map((section: SectionData): SectionData => {
				const width = R.isNil(widths[section.id]) ? section.initialWidth : widths[section.id];
				return { ...section, width };
			}),
	);

	return (state, ownProps) => {
		const sectionState = state.sections[ownProps.id] || sectionStateObj;
		const {
			widths = widthObj,
			totalWidth,
			activeSeparatorIndex,
			hoveredSectionIndex,
		} = sectionState;

		const sectionsData = getSectionsDataSelectorInstance(ownProps.sectionsData, widths);
		return {
			activeSeparatorIndex,
			sectionsData,
			totalWidth,
			isDragging: state.isDragging,
			hoveredSectionIndex,
		};
	};
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps): DispatchProps => ({
	setActiveSeparator: (index: number | null | undefined) => {
		dispatch({
			type: SET_ACTIVE_SEPARATOR,
			id: ownProps.id,
			index,
		});
	},
	setHovered: (index: number | null | undefined) => {
		dispatch({
			type: SET_IS_HOVERED,
			id: ownProps.id,
			index,
		});
	},
	onResize: (payload: OnResizePayload) => {
		dispatch({
			type: ON_RESIZE,
			payload,
		});
	},
	onDrag: (payload: OnDragPayload) => {
		dispatch({
			type: ON_DRAG,
			payload,
		});
	},
	onDragStartOwn: (payload: string) => {
		dispatch({
			type: ON_DRAG_START,
			payload,
		});
	},
	onDragEndOwn: (payload: string) => {
		dispatch({
			type: ON_DRAG_END,
			payload,
		});
	},
	onLengthChange: (payload: OnResizePayload) => {
		dispatch({
			type: ON_LENGTH_CHANGE,
			payload,
		});
	},
	savePreDragWidths: (sectionId: string) => {
		dispatch({
			type: SAVE_PREDRAG_WIDTHS,
			id: ownProps.id,
			sectionId,
		});
	},
	setOnboardingWidth: (id: string, sectionsData: SectionData[], width: number | undefined) => {
		dispatch({
			type: SET_ONBOARDING_WIDTH,
			id,
			sectionsData,
			width,
		});
	},
});

const connect = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	mapStateToPropsFn: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	mapDispatchToPropsFn: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	mergeProps: any = undefined,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options: any = {},
) =>
	connectWrapper(mapStateToPropsFn, mapDispatchToPropsFn, mergeProps, {
		...options,
		context: SectionContext,
		storeKey,
	});

const Connected = connect(mapStateToPropsFactory, mapDispatchToProps, null, { storeKey })(
	({
		innerRef,
		...props
	}: {
		innerRef?: Ref<Sections>;
	} & Props) => <Sections ref={innerRef} {...props} />,
);

const NameSpaced = withNamespacedId(Connected);

/**
 * SectionsSubscriber
 *
 * The purpose of this is to expose the internal redux
 * For example, we need to react to collapsible, one way is to add a onCollapse callback to sections
 * However that is less flexible and not as scalable. Exposing the store will just give us a bit more power to adapt
 * to change. We already have a Provider so might as well have a Subscriber
 *
 * Keeping this subscriber generic, you can create specific selecting subscribers in your actual app to pluck the data you need
 * */

const SubscriberHoc = ({ sectionsData, children }: SubscriberHocProps) =>
	children ? children({ sectionsData }) : null;

export const Subscriber = connect(
	(state: State, { children }: SubscriberOwnProps) => {
		const sectionsData = state.sections || null;
		return {
			sectionsData,
			children,
		};
	},
	null,
	null,
	{
		storeKey,
	},
)(SubscriberHoc);

Object.assign(NameSpaced, {
	Provider,
	Subscriber,
	Container,
	ColumnTexts,
	Item,
});

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export default NameSpaced as typeof NameSpaced & {
	Provider: typeof Provider;
	Subscriber: typeof Subscriber;
	Item: typeof Item;
	Container: typeof Container;
	ColumnTexts: typeof ColumnTexts;
};
